import { useCallback, useEffect, useState } from "react";
import { Download, Share, Smartphone, X, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/** Detect iOS Safari — it doesn't fire `beforeinstallprompt` and needs
 * "Share → Add to Home Screen" instructions instead. */
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/** Standalone / installed detection — hide the button once already installed. */
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true
  );
}

function detectBrowser() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "edge";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Chrome\//.test(ua) && !/OPR\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

/**
 * Download / Install Accountrix
 *
 * • Chromium / Edge / Android: uses the `beforeinstallprompt` API for a
 *   one-click install when Chrome's engagement heuristic has fired.
 * • iOS Safari: shows a helper dialog with "Share → Add to Home Screen".
 * • Everywhere else (or Chrome before the heuristic fires): shows a
 *   browser-specific "how to install" dialog so the button is never
 *   silently missing on the landing page.
 *
 * `variant` = "prominent" (Login page) | "compact" (AppShell sidebar footer)
 */
export function InstallPWA({ variant = "compact", testId = "install-pwa-btn" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [showGenericHelp, setShowGenericHelp] = useState(false);

  useEffect(() => {
    if (installed) return undefined;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  const trigger = useCallback(async () => {
    if (isIOS()) { setShowIOSHelp(true); return; }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
      } catch (err) {
        console.debug("[InstallPWA] user dismissed install prompt:", err?.message);
      }
      setDeferredPrompt(null);
      return;
    }
    // beforeinstallprompt hasn't fired yet — show manual install instructions
    // tailored to the current browser so the button is never a dead-end.
    setShowGenericHelp(true);
  }, [deferredPrompt]);

  // Hide only when the app is already installed. Otherwise ALWAYS render
  // the button so users can discover the install option on the landing
  // page even before Chrome's engagement heuristic fires.
  if (installed) return null;

  if (variant === "prominent") {
    return (
      <>
        <button
          type="button"
          data-testid={testId}
          onClick={trigger}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[var(--brand)] text-[var(--brand)] hover:bg-[var(--brand-light)] transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Download the app · Use offline
        </button>
        <IOSHelpDialog open={showIOSHelp} onClose={() => setShowIOSHelp(false)} />
        <GenericInstallHelpDialog open={showGenericHelp} onClose={() => setShowGenericHelp(false)} />
      </>
    );
  }

  // compact
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={trigger}
        title="Install Accountrix for offline use"
        className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-[var(--brand-text)] bg-[var(--brand-light)] hover:bg-[var(--brand)] hover:text-white transition-colors"
      >
        <Download className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">Install app · offline</span>
      </button>
      <IOSHelpDialog open={showIOSHelp} onClose={() => setShowIOSHelp(false)} />
      <GenericInstallHelpDialog open={showGenericHelp} onClose={() => setShowGenericHelp(false)} />
    </>
  );
}

/** Fallback dialog shown when the browser hasn't (yet) fired the native
 *  beforeinstallprompt event — walks the user through the browser's own
 *  install menu so the "Download the app" button is never a dead-end. */
function GenericInstallHelpDialog({ open, onClose }) {
  const browser = detectBrowser();
  const steps = {
    chrome: [
      "Look at the address bar — you'll see a small install icon on the right side (a monitor with a down arrow).",
      "Or open the ⋮ menu in the top-right and choose \"Install Accountrix…\" / \"Add to Home screen\".",
      "Click Install to add it to your desktop or home screen.",
    ],
    edge: [
      "Look at the address bar — click the small install icon on the right (a monitor with a plus).",
      "Or open the … menu in the top-right → Apps → Install Accountrix.",
      "Click Install to add it to your desktop.",
    ],
    firefox: [
      "Firefox doesn't currently support installing web apps on desktop.",
      "You can still use Accountrix offline in this tab — the service worker caches everything.",
      "For a native install experience, open this page in Chrome, Edge, or Safari.",
    ],
    safari: [
      "In Safari on macOS 14+, click the Share button in the toolbar (square with an up-arrow).",
      "Choose \"Add to Dock\" from the share sheet.",
      "Accountrix will open in its own window from the Dock.",
    ],
    other: [
      "Your browser doesn't have a native install button for this site yet.",
      "Look for a \"Add to Home Screen\", \"Install App\", or \"Install\" option in your browser's menu.",
      "Or open this page in Chrome, Edge, or Safari for a one-click install.",
    ],
  }[browser];
  const heading = {
    chrome: "Install on Chrome",
    edge: "Install on Edge",
    firefox: "Install on Firefox",
    safari: "Install on Safari (Mac)",
    other: "Install this app",
  }[browser];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="install-pwa-generic-help">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[var(--brand)]" /> {heading}
          </DialogTitle>
          <DialogDescription>
            Install Accountrix for offline access, native-app feel, and desktop / home-screen shortcut.
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          {steps.map((text, i) => (
            <li key={`step-${i}`} className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--brand)] text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>
              <span>{text}</span>
            </li>
          ))}
        </ol>
        <div className="flex justify-end pt-2">
          <Button onClick={onClose} data-testid="install-pwa-generic-close">Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IOSHelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="install-pwa-ios-help">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[var(--brand)]" /> Install on iPhone / iPad
          </DialogTitle>
          <DialogDescription>
            iOS only allows installing web apps from Safari itself — here's the two-tap flow.
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--brand)] text-white text-xs flex items-center justify-center shrink-0">1</span>
            <span>Tap the <Share className="inline w-4 h-4 mx-1" /> <span className="font-semibold">Share</span> button at the bottom of Safari.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--brand)] text-white text-xs flex items-center justify-center shrink-0">2</span>
            <span>Scroll and tap <span className="font-semibold">Add to Home Screen</span>.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--brand)] text-white text-xs flex items-center justify-center shrink-0">3</span>
            <span>Tap <span className="font-semibold">Add</span> — you're done. The Accountrix icon appears on your home screen and works offline.</span>
          </li>
        </ol>
        <div className="flex justify-end pt-2">
          <Button onClick={onClose} data-testid="install-pwa-ios-close">Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Persistent banner shown when the browser thinks we're offline. */
export function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  if (online) return null;
  return (
    <div
      data-testid="offline-banner"
      className="fixed top-0 inset-x-0 z-50 bg-[#B4283A] text-white text-xs px-4 py-2 flex items-center justify-center gap-2 shadow-md"
    >
      <WifiOff className="w-3.5 h-3.5" />
      You're offline · viewing cached books. Changes won't save until you reconnect.
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="ml-2 underline underline-offset-2 hover:opacity-90"
      >Retry</button>
      <span className="hidden"><X /></span>
    </div>
  );
}
